import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import {
  deriveOrgSize,
  calculateRecommendedPolicies,
  matchCatalogToTemplates
} from '../../../shared/utils/policyInventoryEngine'
import { enrichPolicy } from '../../../shared/utils/policyAI'

export function usePolicyInventory(client) {
  const { user } = useAuth()
  const [recommendations, setRecommendations] = useState([])
  const [calculatedPolicies, setCalculatedPolicies] = useState(null)
  const [templateMatches, setTemplateMatches] = useState(new Map())
  const [stats, setStats] = useState({
    adopted: 0, inProgress: 0, gap: 0,
    totalRecommended: 0, totalConsolidated: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initializedRef = useRef(false)

  const clientId = client?.id
  const frameworks = Array.isArray(client?.regulatoryFrameworks)
    ? client.regulatoryFrameworks
    : Array.isArray(client?.regulatory_frameworks)
      ? client.regulatory_frameworks
      : []

  const loadData = useCallback(async () => {
    if (!clientId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Derive size and calculate recommended policies
      const size = deriveOrgSize(client.staffingSize || client.staffing_size, client.annualRevenue || client.annual_revenue)
      const engineResult = calculateRecommendedPolicies({
        industry: client.industry,
        size,
        frameworks
      })
      setCalculatedPolicies(engineResult)

      // Fetch templates, recommendations, and client policies in parallel
      const [templatesRes, recsRes, clientPoliciesRes] = await Promise.all([
        supabase.from('policy_templates').select('*').eq('global_template', false),
        supabase
          .from('client_policy_recommendations')
          .select('*')
          .eq('client_id', clientId),
        supabase
          .from('client_policies')
          .select('id, title, domain, status, template_id')
          .eq('client_id', clientId)
      ])

      if (templatesRes.error) throw templatesRes.error
      if (recsRes.error) throw recsRes.error
      if (clientPoliciesRes.error) throw clientPoliciesRes.error

      const templates = templatesRes.data || []
      let recs = recsRes.data || []
      const clientPolicies = clientPoliciesRes.data || []

      // Match catalog to templates
      const matches = matchCatalogToTemplates(engineResult.policies, templates)
      setTemplateMatches(matches)

      // Auto-generate recommendations for any missing policies
      // Uses upsert so existing adopted/in_progress recs are preserved
      const existingCatalogIds = new Set(recs.map(r => r.catalog_policy_id))
      const missingPolicies = engineResult.policies.filter(p => !existingCatalogIds.has(p.id))

      if (missingPolicies.length > 0 && !initializedRef.current) {
        initializedRef.current = true
        const inserts = missingPolicies.map(p => ({
          client_id: clientId,
          catalog_policy_id: p.id,
          status: 'gap',
          is_consolidated: p.consolidated,
          consolidated_into: p.consolidatedInto
        }))

        const { data: inserted, error: insertErr } = await supabase
          .from('client_policy_recommendations')
          .upsert(inserts, { onConflict: 'client_id,catalog_policy_id' })
          .select()

        if (insertErr) throw insertErr
        // Merge newly inserted with existing recs
        recs = [...recs, ...(inserted || [])]
      }

      // Auto-detect adoption from existing client_policies
      const updatedRecs = []
      for (const rec of recs) {
        const catalogPolicy = engineResult.policies.find(p => p.id === rec.catalog_policy_id)
        if (!catalogPolicy) continue

        let updatedRec = { ...rec }

        // Check if there's an existing client policy matching this domain
        if (!rec.linked_client_policy_id) {
          const matchingClientPolicy = clientPolicies.find(
            cp => cp.domain === catalogPolicy.domain
          )
          if (matchingClientPolicy) {
            const newStatus = matchingClientPolicy.status === 'published' || matchingClientPolicy.status === 'acknowledged'
              ? 'adopted'
              : 'in_progress'

            await supabase
              .from('client_policy_recommendations')
              .update({
                linked_client_policy_id: matchingClientPolicy.id,
                status: newStatus
              })
              .eq('id', rec.id)

            updatedRec.linked_client_policy_id = matchingClientPolicy.id
            updatedRec.status = newStatus
          }
        }

        // Enrich with catalog data
        updatedRec._catalog = catalogPolicy
        updatedRecs.push(updatedRec)
      }

      setRecommendations(updatedRecs)

      // Compute stats (non-consolidated only)
      const nonConsolidated = updatedRecs.filter(r => !r.is_consolidated)
      setStats({
        adopted: nonConsolidated.filter(r => r.status === 'adopted').length,
        inProgress: nonConsolidated.filter(r => r.status === 'in_progress').length,
        gap: nonConsolidated.filter(r => r.status === 'gap').length,
        totalRecommended: engineResult.totalRecommended,
        totalConsolidated: engineResult.totalConsolidated
      })
    } catch (err) {
      console.error('[usePolicyInventory] Error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [clientId, client?.staffingSize, client?.staffing_size, client?.annualRevenue, client?.annual_revenue, client?.industry, frameworks.join(',')])

  useEffect(() => {
    initializedRef.current = false
    loadData()
  }, [loadData])

  const updateStatus = useCallback(async (recommendationId, newStatus) => {
    const { error: err } = await supabase
      .from('client_policy_recommendations')
      .update({ status: newStatus })
      .eq('id', recommendationId)

    if (err) throw err
    await loadData()
  }, [loadData])

  const linkPolicy = useCallback(async (recommendationId, clientPolicyId) => {
    const { error: err } = await supabase
      .from('client_policy_recommendations')
      .update({ linked_client_policy_id: clientPolicyId })
      .eq('id', recommendationId)

    if (err) throw err
    await loadData()
  }, [loadData])

  const copyTemplateToClient = useCallback(async (catalogPolicyId, { skipEnrichment = false } = {}) => {
    const template = templateMatches.get(catalogPolicyId)
    if (!template) throw new Error('No matching template found')

    // Attempt AI enrichment if client has regulatory frameworks
    let finalContent = template.content
    let changeSummary = 'Copied from policy library'
    let wasEnriched = false

    if (!skipEnrichment && frameworks.length > 0 && template.content) {
      // Gather consolidated child policies that were merged into this parent
      const consolidatedChildren = recommendations
        .filter(r => r.consolidated_into === catalogPolicyId && r._catalog)
        .map(r => ({
          name: r._catalog.name,
          description: r._catalog.description,
          requiredBy: r._catalog.requiredBy || []
        }))

      try {
        const result = await enrichPolicy({
          content: template.content,
          domain: template.domain,
          frameworks,
          consolidatedPolicies: consolidatedChildren.length > 0 ? consolidatedChildren : undefined,
          clientContext: {
            clientName: client?.name,
            industry: client?.industry,
            staffingSize: client?.staffingSize || client?.staffing_size,
            annualRevenue: client?.annualRevenue || client?.annual_revenue
          }
        })
        if (result.enriched && result.content) {
          finalContent = result.content
          changeSummary = `Copied from policy library and enriched for ${frameworks.join(', ')}`
          wasEnriched = true
        }
      } catch (err) {
        console.warn('[usePolicyInventory] Enrichment failed, using base template:', err.message)
        // Fall through — use base template content
      }
    }

    // Insert client_policy
    const { data: newPolicy, error: cpErr } = await supabase
      .from('client_policies')
      .insert({
        client_id: clientId,
        template_id: template.id,
        title: template.title,
        domain: template.domain,
        content: finalContent,
        structured_content: template.structured_content || null,
        framework_tags: template.framework_tags || [],
        status: 'draft'
      })
      .select()
      .single()

    if (cpErr) throw cpErr

    // Create v1 version
    await supabase
      .from('policy_versions')
      .insert({
        client_policy_id: newPolicy.id,
        version_number: 1,
        content: finalContent,
        change_summary: changeSummary,
        created_by: user.id
      })

    // Link recommendation
    const rec = recommendations.find(r => r.catalog_policy_id === catalogPolicyId)
    if (rec) {
      await supabase
        .from('client_policy_recommendations')
        .update({
          linked_client_policy_id: newPolicy.id,
          status: 'in_progress'
        })
        .eq('id', rec.id)
    }

    await loadData()
    return { enriched: wasEnriched }
  }, [clientId, client, frameworks, templateMatches, recommendations, user?.id, loadData])

  const copyAllToClient = useCallback(async () => {
    const toCopy = recommendations.filter(r =>
      !r.is_consolidated &&
      !r.linked_client_policy_id &&
      templateMatches.get(r.catalog_policy_id)
    )

    let copied = 0
    for (const rec of toCopy) {
      try {
        await copyTemplateToClient(rec.catalog_policy_id, { skipEnrichment: true })
        copied++
      } catch (err) {
        console.error(`[usePolicyInventory] Failed to copy ${rec.catalog_policy_id}:`, err)
      }
    }

    return { copied, total: toCopy.length }
  }, [recommendations, templateMatches, copyTemplateToClient])

  const recalculate = useCallback(async () => {
    initializedRef.current = false

    // Delete gaps and stale "in_progress" entries that have no linked client policy
    const stale = recommendations.filter(r =>
      r.status === 'gap' || (r.status === 'in_progress' && !r.linked_client_policy_id)
    )
    if (stale.length > 0) {
      await supabase
        .from('client_policy_recommendations')
        .delete()
        .in('id', stale.map(r => r.id))
    }

    await loadData()
  }, [recommendations, loadData])

  return {
    recommendations,
    calculatedPolicies,
    templateMatches,
    stats,
    loading,
    error,
    updateStatus,
    linkPolicy,
    copyTemplateToClient,
    copyAllToClient,
    recalculate
  }
}
